import { Image } from '@customerio/pluma-components/react';
import type { PaymentCard } from '@/services/billing/types';
import { isEmptyString } from '@/utils/string';
import brandVisa from '@/assets/images/credit-cards/brand-visa.svg';
import brandAmex from '@/assets/images/credit-cards/brand-amex.svg';
import brandDiscover from '@/assets/images/credit-cards/brand-discover.svg';
import brandMastercard from '@/assets/images/credit-cards/brand-mastercard.svg';
import brandUnknown from '@/assets/images/credit-cards/brand-unknown.svg';
import * as styles from './credit-card-details.css';

const CARD_BRAND_LOGOS: Record<string, string> = {
  visa: brandVisa,
  amex: brandAmex,
  discover: brandDiscover,
  mastercard: brandMastercard,
};

type CreditCardDetailsProps = {
  /** The account's saved card in the vendor-neutral shape, or null when none is on file. */
  card: PaymentCard | null;
  /** Whether a real card is on file. When false the card renders dimmed with placeholder values. */
  hasCard: boolean;
  /** Account display name, shown on the card face. */
  accountName: string;
  /** Account logo URL (already resolved to a fallback by the caller). */
  accountLogoUrl: string;
};

/**
 * The visual representation of the account's saved credit card. Reads the vendor-neutral
 * {@link PaymentCard} (the billing service resolves Stripe vs. Ordway), and falls back to a dimmed
 * placeholder card ("Jane Doe", masked digits) when no card is on file.
 */
export function CreditCardDetails({ card, hasCard, accountName, accountLogoUrl }: CreditCardDetailsProps) {
  const last4 = card?.last4 ?? '';
  const expiry = card?.expiry ?? '';
  const displayLast4 = isEmptyString(last4) ? '****' : last4;
  const displayExpiry = isEmptyString(expiry) ? 'XX/XXXX' : expiry;
  const displayOwnerName = hasCard ? (card?.ownerName ?? '') : 'Jane Doe';

  const brand = (card?.brand ?? '').toLowerCase();
  const brandLogo = CARD_BRAND_LOGOS[brand] ?? brandUnknown;
  const brandAlt = brand in CARD_BRAND_LOGOS ? `Card logo for ${brand}` : 'Placeholder for a credit card logo';

  const cardClassName = hasCard ? styles.card : `${styles.card} ${styles.cardDimmed}`;
  const companyClassName = hasCard ? styles.company : `${styles.company} ${styles.companyDimmed}`;

  return (
    <div data-test-credit-card-details="" className={cardClassName}>
      <div data-test-credit-card-details="account-name" className={companyClassName}>
        <Image
          src={accountLogoUrl}
          alt="Uploaded account logo"
          className={styles.companyLogo}
          style={{ width: '24px' }}
        />
        {accountName}
      </div>

      <div data-test-credit-card-details="last4" className={styles.last4}>
        **** **** **** {displayLast4}
      </div>

      <div className={styles.footer}>
        <div className={styles.details}>
          <div data-test-credit-card-details="name" className={styles.ownerName}>
            {displayOwnerName}
          </div>
          <div data-test-credit-card-details="expiry" className={styles.expiry}>
            {displayExpiry}
          </div>
        </div>

        <div data-test-credit-card-details={isEmptyString(brand) ? 'unknown' : brand} className={styles.brand}>
          <Image src={brandLogo} alt={brandAlt} />
        </div>
      </div>
    </div>
  );
}
